'use client';

import { useState, useEffect, useCallback } from 'react';
import { ROLE_DISPLAY_LABELS, type UserRole } from '@/contexts/AuthContext';
import TempPasswordModal from './TempPasswordModal';

interface UnprovisionedEmployee {
  id: string;
  first_name: string;
  last_name: string;
  job_title: string;
  department: string;
  work_email: string | null;
  work_phone: string | null;
  roles: string[];
}

const AVATAR_COLORS = [
  'bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-orange-600',
  'bg-pink-600', 'bg-teal-600', 'bg-indigo-600', 'bg-red-600',
];

const PendingProvisioningPanel = ({ onProvisioned }: { onProvisioned: () => void }) => {
  const [unprovisioned, setUnprovisioned] = useState<UnprovisionedEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [provisioningId, setProvisioningId] = useState<string | null>(null);
  const [credentialsModal, setCredentialsModal] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const loadUnprovisioned = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    if (signal?.aborted) return;

    try {
      const r = await fetch('/api/rbac/list-unprovisioned-employees', { signal });
      if (signal?.aborted) return;
      
      const data = await r.json();
      
      if (!r.ok || data.error) {
        console.warn(`[PendingProv] API failed: ${data.error ?? r.statusText}`);
        setUnprovisioned([]);
        setLoading(false);
        return;
      }
      
      if (signal?.aborted) return;
      console.log(`[PendingProv] Loaded ${data.unprovisioned_count} unprovisioned employees`);
      setUnprovisioned(data.unprovisioned ?? []);
      setLoading(false);
    } catch (err) {
      if (signal?.aborted) return;
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : String(err);
      console.warn(`[PendingProv] Fetch failed: ${msg}`);
      setUnprovisioned([]);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadUnprovisioned(controller.signal);
    return () => { controller.abort(); };
  }, [loadUnprovisioned]);

  const handleProvision = async (emp: UnprovisionedEmployee) => {
    if (provisioningId) return;

    if (emp.roles.length === 0) {
      setErrorMsg(`${emp.first_name} has no roles assigned. Edit their employee record first.`);
      setTimeout(() => setErrorMsg(null), 5000);
      return;
    }

    setProvisioningId(emp.id);
    setErrorMsg(null);
    console.log(`[PendingProv] Provisioning ${emp.first_name} ${emp.last_name}`);

    try {
      const r = await fetch('/api/rbac/provision-employee', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: emp.id }),
      });
      const data = await r.json();

      if (!r.ok || data.error) {
        setErrorMsg(`Provision failed: ${data.error ?? 'Unknown error'}`);
        setTimeout(() => setErrorMsg(null), 6000);
        setProvisioningId(null);
        return;
      }

      console.log(`[PendingProv] Success:`, data);

      setCredentialsModal({
        action: 'provisioned',
        user_name: `${emp.first_name} ${emp.last_name}`,
        email: data.email,
        temp_password: data.temp_password,
        message: data.message,
      });

      // Refresh
      await loadUnprovisioned();
      onProvisioned();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(`Provision failed: ${msg}`);
      setTimeout(() => setErrorMsg(null), 6000);
    } finally {
      setProvisioningId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-5">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <div className="w-4 h-4 rounded-full border-2 border-[#E89A3C] border-t-transparent animate-spin" />
          Loading unprovisioned employees...
        </div>
      </div>
    );
  }

  if (unprovisioned.length === 0) {
    return null; // Nothing to show
  }

  return (
    <>
      {errorMsg && (
        <div className="mb-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-[12px] text-red-700 font-medium">
          {errorMsg}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden mb-5">
        <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-5 py-3 border-b border-amber-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h3 className="text-[14px] font-bold text-amber-900">
              Pending Provisioning — {unprovisioned.length}
            </h3>
          </div>
          <p className="text-[10px] text-amber-700">Employees without logins</p>
        </div>

        <div className="divide-y divide-gray-100">
          {unprovisioned.map((emp, idx) => (
            <div key={emp.id} className="px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}
                >
                  {`${emp.first_name[0]}${emp.last_name[0]}`.toUpperCase()}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="text-[13px] font-bold text-[#1C2E5E]">
                      {emp.first_name} {emp.last_name}
                    </p>
                    <span className="text-[11px] text-gray-500">{emp.job_title}</span>
                    <span className="text-[10px] text-gray-400">· {emp.department}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {emp.roles.length === 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-700">
                        NO ROLES
                      </span>
                    ) : (
                      emp.roles.map(r => (
                        <span
                          key={r}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-blue-50 text-blue-700"
                        >
                          {ROLE_DISPLAY_LABELS[r as UserRole] ?? r}
                        </span>
                      ))
                    )}
                  </div>
                  {emp.work_email ? (
                    <p className="text-[10px] text-gray-500 mt-1">Email will be set to: {emp.work_email}</p>
                  ) : (
                    <p className="text-[10px] text-amber-600 mt-1">
                      No work_email set; will use {emp.first_name.toLowerCase()}.{emp.last_name.toLowerCase()}@getnextroof.com
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handleProvision(emp)}
                  disabled={provisioningId === emp.id || emp.roles.length === 0}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors flex-shrink-0"
                  title={
                    emp.roles.length === 0
                      ? 'No roles assigned — edit employee first' :'Create login + temp password'
                  }
                >
                  {provisioningId === emp.id ? 'Provisioning…' : '+ Provision Login'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TempPasswordModal data={credentialsModal} onClose={() => setCredentialsModal(null)} />
    </>
  );
};

export default PendingProvisioningPanel;
